import React, { useEffect, useRef, useState } from "react"
import {
  Box, Checkbox, Divider, Flex, Text,
} from "@chakra-ui/react"
import { Combobox, useCombobox } from "components/Combobox"
import useInitialSelectedFirst from "components/Combobox/useInitialSelectedFirst"
import Icon from "components/elements/Icon"
import { FilterValueOption } from "./types"

// Native-scroll styling matched to the SimpleBar look used elsewhere
// (Menu, DataTable). Native is required here so the row's max-content
// width propagates up to PopoverContent — SimpleBar's absolutely-
// positioned mask breaks that chain and the popover can't grow with the
// longest label.
const SCROLLBAR_SX = {
  scrollbarWidth: "thin" as const,
  scrollbarColor: "rgb(127,127,127) transparent",
  "&::-webkit-scrollbar": { width: "11px", height: "11px", background: "transparent" },
  "&::-webkit-scrollbar-track": { background: "transparent" },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "rgb(127,127,127)",
    backgroundClip: "padding-box",
    border: "2px solid transparent",
    borderRadius: "7px",
  },
}

const APPLY_FOOTER_STYLE = {
  // role="button" because Apply commits, not a listbox option.
  role: "button" as const,
  py: 2,
  px: 0,
  bg: "transparent",
  borderTopWidth: 1,
  borderColor: "gray.200",
  justifyContent: "center" as const,
  cursor: "pointer" as const,
  transition: "background-color 0.2s ease",
  // :active after :hover so click+hold wins the same-specificity tie.
  // data-highlighted is qualified with :not(:hover):not(:active) so
  // pointer interactions own pointer states; it only paints when the
  // user arrow-keyed onto Apply from the rows above.
  sx: {
    "&:hover": { bg: "blue.50" },
    "&:active": { bg: "blue.100" },
    "&[data-highlighted]:not(:hover):not(:active)": { bg: "blue.50" },
  },
}

// Linear-style hybrid: row click commits a single value and closes (single
// mode); checkbox click enters multi-mode and accumulates locally until
// Apply. In edit mode with no values left after unchecking, the chip is
// torn down via onCommit([]).

type Props = {
  label: string
  options: FilterValueOption[]
  // Synthesised entries for stored values whose option no longer exists
  // (e.g. saved segment references a tag the user has since deleted).
  // Rendered after a divider in muted/italic style so the user can
  // uncheck them and clean up the chip.
  orphans?: FilterValueOption[]
  initialValues?: string[]
  onCommit: (values: string[]) => void
  // When true, clicking the checkbox commits that single value immediately
  // instead of entering multi-mode. Used for fields whose values are
  // mutually exclusive (e.g. Subscribed / Unsubscribed).
  singleSelect?: boolean
  // When true, neither row clicks nor the Apply button close the outer
  // popover on commit. Used when MultiValuePicker is an intermediate step
  // (e.g. form selection in the Form Submission flow) rather than the
  // final step.
  keepOpenOnCommit?: boolean
}

const arraysSameSet = (a: string[], b: string[]) => {
  if (a.length !== b.length) return false
  const aSet = new Set(a)

  return b.every((v) => aSet.has(v))
}

const MultiValuePicker = ({
  label, options, orphans = [], initialValues = [], onCommit, singleSelect = false,
  keepOpenOnCommit = false,
}: Props) => {
  const { query, inputRef, isOpen } = useCombobox()
  const [selected, setSelected] = useState<string[]>(initialValues)
  // Reopened chips start in single-commit mode — checkbox click is the
  // affordance to enter multi-mode and accumulate.
  const [multiMode, setMultiMode] = useState(false)
  // Anchor for shift-click range select. Stored as value, not index, so it
  // survives search-input filtering.
  const lastClickedValueRef = useRef<string | null>(null)

  // Combobox.Root's focus effect misses the step-1 → step-2 transition
  // (registerInput false→true batches, hasInput doesn't change); force it.
  useEffect(() => {
    let frame2 = 0
    const frame1 = window.requestAnimationFrame(() => {
      frame2 = window.requestAnimationFrame(() => {
        inputRef.current?.focus()
      })
    })

    return () => {
      window.cancelAnimationFrame(frame1)
      window.cancelAnimationFrame(frame2)
    }
  }, [inputRef])

  // Only auto-tear-down for chips that already had values, so the add
  // flow doesn't spuriously commit an empty array on mount.
  const initiallyHadValues = useRef(initialValues.length > 0)
  useEffect(() => {
    if (multiMode && selected.length === 0 && initiallyHadValues.current) {
      onCommit([])
    }
  }, [multiMode, selected.length, onCommit])

  const filtered = options.filter(
    (o) => o.label.toLowerCase().includes(query.toLowerCase()),
  )

  const toggle = (value: string) => {
    setSelected((curr) => (
      curr.includes(value) ? curr.filter((v) => v !== value) : [...curr, value]
    ))
  }

  const isDirty = !arraysSameSet(selected, initialValues)
  const showApply = multiMode && selected.length > 0 && isDirty

  // Net add+remove count vs initial, not total selected — "Apply (1)"
  // would be noise so we hide the count when dirty=1.
  const dirtyCount = (() => {
    const initSet = new Set(initialValues)
    const selSet = new Set(selected)
    let count = 0
    selected.forEach((v) => { if (!initSet.has(v)) count += 1 })
    initialValues.forEach((v) => { if (!selSet.has(v)) count += 1 })

    return count
  })()

  // Initially-selected options sort to the top with a divider before the
  // rest — shared logic with Combobox.List's `pinSelected` so the UX is
  // consistent across every multi-select picker.
  const { items: orderedFiltered, divider } = useInitialSelectedFirst(
    filtered,
    (o) => o.value,
    new Set(initialValues),
    query === "",
    isOpen,
  )

  const renderRow = (option: FilterValueOption) => {
    const isChecked = selected.includes(option.value)
    // Unchecked rows in single-commit mode hide the checkbox until row
    // hover.
    const checkboxAlwaysShown = multiMode || isChecked

    return (
      <Combobox.Action
        key={option.value}
        onSelect={() => {
          if (multiMode) {
            toggle(option.value)

            return
          }
          // Single-click path toggles against the chip's current values
          // and commits immediately. Row A on chip [A] becomes [] which
          // FilterChipValue maps to onRemove.
          const next = initialValues.includes(option.value)
            ? initialValues.filter((v) => v !== option.value)
            : [...initialValues, option.value]
          onCommit(next)
        }}
        closeOnSelect={!multiMode && !keepOpenOnCommit}
        px={0}
        py={0}
        sx={checkboxAlwaysShown ? undefined : {
          "& .row-checkbox": { visibility: "hidden" },
          "&:hover .row-checkbox": { visibility: "visible" },
        }}
      >
        <Flex align="center" gap={2} px={3} py={2} w="full">
          {!singleSelect && (
            <Box
              className="row-checkbox"
              w={5}
              flexShrink={0}
              display="flex"
              alignItems="center"
              justifyContent="center"
              position="relative"
              onClick={(e) => {
                e.stopPropagation()
                setMultiMode(true)
                const lastValue = lastClickedValueRef.current
                const lastIndex = lastValue
                  ? filtered.findIndex((o) => o.value === lastValue)
                  : -1
                const currentIndex = filtered.findIndex(
                  (o) => o.value === option.value,
                )
                if (e.shiftKey && lastIndex !== -1 && lastIndex !== currentIndex) {
                  const [from, to] = lastIndex < currentIndex
                    ? [lastIndex, currentIndex]
                    : [currentIndex, lastIndex]
                  const rangeValues = filtered.slice(from, to + 1).map(
                    (o) => o.value,
                  )
                  setSelected((curr) => (
                    Array.from(new Set([...curr, ...rangeValues]))
                  ))
                } else {
                  toggle(option.value)
                }
                lastClickedValueRef.current = option.value
              }}
              // Forgiving hit area: extends the checkbox click target 8px
              // past the visible box on all sides so a slightly-off click
              // still enters multi-mode + toggles instead of falling
              // through to the row's single-commit path.
              _before={{
                content: "\"\"",
                position: "absolute",
                top: "-8px",
                right: "-8px",
                bottom: "-8px",
                left: "-8px",
              }}
            >
              <Checkbox
                isChecked={isChecked}
                colorScheme="blue"
                pointerEvents="none"
                tabIndex={-1}
              />
            </Box>
          )}
          {option.renderIcon
            ? option.renderIcon({ boxSize: 4 })
            : option.iconColor && (
              <Icon
                icon={option.icon ?? "dot"}
                size={3}
                color={option.iconColor}
              />
            )}
          <Text fontSize="md" data-testid={`filter-value-${option.value}`}>
            {option.label}
          </Text>
        </Flex>
      </Combobox.Action>
    )
  }

  return (
    <Box>
      <Combobox.Input placeholder={`Search ${label}`} py={2} />
      <Box maxH="70vh" overflowY="auto" py={1} sx={SCROLLBAR_SX}>
        {filtered.length === 0 ? (
          <Box px={3} py={4} textAlign="center" fontSize="md" color="gray.500">
            {query ? `No matches for "${query}"` : "No options available"}
          </Box>
        ) : (
          orderedFiltered.map((option, index) => (
            <React.Fragment key={option.value}>
              {divider > 0 && index === divider && <Divider my={1} />}
              {renderRow(option)}
            </React.Fragment>
          ))
        )}
        {/* Orphans (values whose option no longer exists) sit at the
            bottom under a divider so the user can uncheck and clean up
            the chip. Hidden while the search input is active — they
            don't carry searchable text. */}
        {orphans.length > 0 && query === "" && (
          <>
            <Divider my={1} />
            <Box px={3} py={1} fontSize="xs" color="gray.500" textTransform="uppercase">
              No longer available
            </Box>
            {orphans.map((orphan) => {
              const isChecked = selected.includes(orphan.value)

              return (
                <Combobox.Action
                  key={`orphan-${orphan.value}`}
                  onSelect={() => {
                    setMultiMode(true)
                    toggle(orphan.value)
                  }}
                  closeOnSelect={false}
                  px={0}
                  py={0}
                >
                  <Flex align="center" gap={2} px={3} py={2} w="full">
                    <Box
                      w={5}
                      flexShrink={0}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      onClick={(e) => {
                        e.stopPropagation()
                        setMultiMode(true)
                        toggle(orphan.value)
                      }}
                    >
                      <Checkbox
                        isChecked={isChecked}
                        colorScheme="blue"
                        pointerEvents="none"
                        tabIndex={-1}
                      />
                    </Box>
                    <Text
                      fontSize="md"
                      fontStyle="italic"
                      color="gray.500"
                      title={orphan.value}
                    >
                      {orphan.label}
                    </Text>
                  </Flex>
                </Combobox.Action>
              )
            })}
          </>
        )}
      </Box>
      {showApply && (
        <Combobox.Action
          onSelect={() => onCommit(selected)}
          closeOnSelect={!keepOpenOnCommit}
          {...APPLY_FOOTER_STYLE}
        >
          <Text color="blue.500" fontWeight="semibold">
            {dirtyCount === 1 ? "Apply" : `Apply (${dirtyCount})`}
          </Text>
        </Combobox.Action>
      )}
    </Box>
  )
}

export default MultiValuePicker
